import React from 'react'
import { MediaBlock } from '@/blocks/MediaBlock/Component'
import { DefaultNodeTypes, SerializedBlockNode } from '@payloadcms/richtext-lexical'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import {
  JSXConvertersFunction,
  RichText as RichTextWithoutBlocks,
} from '@payloadcms/richtext-lexical/react'

import { CodeBlock, CodeBlockProps } from '@/blocks/Code/Component'

import type {
  BannerBlock as BannerBlockProps,
  CallToActionBlock as CTABlockProps,
  MediaBlock as MediaBlockProps,
} from '@/payload-types'
import { BannerBlock } from '@/blocks/Banner/Component'
import { CallToActionBlock } from '@/blocks/CallToAction/Component'
import { cn } from '@/utilities/cn'

type NodeTypes =
  | DefaultNodeTypes
  | SerializedBlockNode<CTABlockProps | MediaBlockProps | BannerBlockProps | CodeBlockProps>

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  blocks: {
    banner: ({ node }) => <BannerBlock className="col-start-2 mb-4" {...node.fields} />,
    mediaBlock: ({ node }) => (
      <MediaBlock
        className="col-start-1 col-span-3"
        imgClassName="m-0"
        {...node.fields}
        captionClassName="mx-auto max-w-[48rem]"
        enableGutter={false}
        disableInnerContainer={true}
      />
    ),
    code: ({ node }) => <CodeBlock className="col-start-2" {...node.fields} />,
    cta: ({ node }) => <CallToActionBlock {...node.fields} />,
  },
})

type Props = {
  data: SerializedEditorState
  enableGutter?: boolean
  enableProse?: boolean
} & React.HTMLAttributes<HTMLDivElement>

// Deep clone and sanitize the AST to remove non-breaking spaces (\u00a0)
// This prevents layout blowouts caused by pasted rich text containing &nbsp;
function sanitizeAST(data: SerializedEditorState): SerializedEditorState {
  if (!data || !data.root) return data

  const processNode = (node: any): any => {
    // If it's a text node, replace non-breaking spaces with regular spaces
    if (node.type === 'text' && typeof node.text === 'string') {
      return {
        ...node,
        text: node.text.replace(/\u00a0/g, ' '),
      }
    }
    // If it has children, process them recursively
    if (node.children && Array.isArray(node.children)) {
      return {
        ...node,
        children: node.children.map(processNode),
      }
    }
    return node
  }

  return {
    ...data,
    root: processNode(data.root),
  }
}

export const RichText: React.FC<Props> = (props) => {
  const { className, enableProse = true, enableGutter = true, data, ...rest } = props
  
  // Sanitize the data before passing it to the renderer
  const sanitizedData = React.useMemo(() => sanitizeAST(data), [data])

  return (
    <RichTextWithoutBlocks
      converters={jsxConverters}
      data={sanitizedData}
      className={cn(
        // Add strong CSS word-break constraints to prevent flex/grid blowouts globally
        'break-words [word-break:break-word] overflow-hidden max-w-full',
        {
          'container ': enableGutter,
          'max-w-none': !enableGutter,
          'mx-auto prose md:prose-md dark:prose-invert ': enableProse,
        },
        className,
      )}
      {...rest}
    />
  )
}
